import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Paper, Typography, Collapse, IconButton } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ExperienceContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
}));

const PaperContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: `${theme.palette.background.paper}CC`,
  boxShadow: `0px 4px 20px ${theme.palette.grey[300]}`,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  position: 'relative',
  paddingRight: theme.spacing(7), // Add right padding to prevent overlap with expand icon
}));

const ExpandIcon = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const experiences = [
  {
    period: "May - July 2023",
    company: "Jio Platforms Ltd.",
    role: "Software Development Intern",
    details: "During my internship at Jio Platforms, I embarked on a multifaceted project that centered around data collection, preprocessing, and analysis with a focus on cricket and beauty product trends. I efficiently organized diverse cricket datasets and developed user-friendly APIs for easy access to this processed data, significantly enhancing project efficiency. Additionally, I delved into analyzing user activity data to glean insights into beauty product trends, aiding in understanding market dynamics. My experimentation with various regression models to predict product popularity showcased my ability to apply theoretical knowledge to practical challenges, leading to the creation of accurate predictive models. This experience enriched my skills in data science, offering a solid foundation for future endeavors in the field."
  },
  {
    period: "Oct 2022 - Feb 2023",
    company: "Fenmo: Meaningful Short Reels",
    role: "Software Developer",
    details: "In my role as the founding developer at Fenmo, I spearheaded the Flutter frontend development, focusing on creating intuitive user interfaces and optimizing user experience. I oversaw the integration of backend services, implementing Firebase and Twilio to ensure robust app functionality. Leveraging Retool, I automated various processes, significantly streamlining operations and facilitating the creation of dynamic admin dashboards. Additionally, I managed the deployment of our app on Google Play, coordinating with AWS to ensure scalability and security. This experience honed my technical leadership skills, deepening my expertise in frontend and backend development, automation, and deployment strategies, setting a strong foundation for my future projects."
  },
  {
    period: "July - Sept 2022",
    company: "Coachbuddy",
    role: "Software Developer",
    details: "In my role as the founding developer at CoachBuddy, I was pivotal in transitioning offline coaching services to an online platform, a venture initiated by a college alumnus. Working entirely remotely, I focused on the Node.js backend, meticulously designing data schemas and tables tailored to the platform's unique needs. I developed RESTful APIs, ensuring they met functionality requirements and were rigorously tested using Postman for reliability. This work not only showcased my ability to adapt and innovate within the digital transformation space but also demonstrated my proficiency in backend development, API construction, and the practical application of testing tools to deliver a seamless online coaching experience."
  },
  {
    period: "April - June 2022",
    company: "Newera Online Education",
    role: "Software Developer",
    details: "As an intern, I developed scripts for migrating data from YouTube to Vimeo, updating the frontend design and code to support this transition. My contributions extended to enhancing user engagement by integrating new functionalities such as the ability to like and comment, thereby improving the platform's interactivity and user experience."
  },
] 

const ExperienceSection = () => {
  const [expanded, setExpanded] = useState(Array(experiences.length).fill(false));

  const handleExpandClick = (index) => {
    setExpanded(expanded.map((state, i) => (i === index ? !state : state)));
  }; 
  return (
    <ExperienceContainer id="experienceSection">
      <Timeline position="alternate">
        {experiences.map((experience, index) => (
          <TimelineItem key={index}>
            <TimelineOppositeContent>
              <Typography variant="body2" color="textSecondary">{experience.period}</Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              {index < experiences.length - 1 && <TimelineConnector />} {/* No connector after the last item */}
            </TimelineSeparator>
            <TimelineContent>
              <PaperContainer elevation={3} onClick={() => handleExpandClick(index)}>
                <Typography variant="h6" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {experience.company}
                </Typography>
                <Typography variant="subtitle1">{experience.role}</Typography>
                <Collapse in={expanded[index]}>
                  <Typography variant="body2">
                    {experience.details}
                  </Typography>
                </Collapse>
                <ExpandIcon
                  onClick={(event) => {
                    event.stopPropagation(); // Prevents the PaperContainer onClick from being triggered
                    handleExpandClick(index);
                  }}
                  aria-expanded={expanded[index]}
                  aria-label="Show more"
                >
                  <ExpandMoreIcon />
                </ExpandIcon>
              </PaperContainer>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </ExperienceContainer>
  );
};

export default ExperienceSection;